import { memo, useMemo } from 'react';

import { Link } from '@tanstack/react-router';
import { StarIcon } from 'lucide-react';

import type { RemoveItemHandler } from '@/_components';
import { Button, FilledStar, IconLogoAvatar } from '@/_components';
import { DeleteButton } from '@/_containers';
import { useContactFormContext } from '@/_pages/contacts/shared';
import type { CompanyQueryLinkerWithPivotSchema } from '@/_schemas/companies/company-query-linker-with-pivot-schema';

export type CompanyItemProps = {
  company: CompanyQueryLinkerWithPivotSchema;
  isEditable?: boolean;
  removeItemHandler: RemoveItemHandler<CompanyQueryLinkerWithPivotSchema>;
  canSetPrimary?: boolean;
};

function CompanyItemBase({ company, canSetPrimary = true, isEditable = false, removeItemHandler }: CompanyItemProps) {
  const { id, name, logo } = company;
  const fullName = name;
  const form = useContactFormContext();

  const [primary_client] = form.watch(['primary_client']);
  const primaryClients = useMemo(() => primary_client || [], [primary_client]);
  const primaryClientMap = useMemo(
    () =>
      primaryClients.reduce(
        (acc: Record<number, CompanyQueryLinkerWithPivotSchema>, item: CompanyQueryLinkerWithPivotSchema) => {
          acc[item.id] = item;
          return acc;
        },
        {}
      ),
    [primaryClients]
  );

  const isPrimary = !!primaryClientMap[company.id];

  const handleDeleteContact = async () => {
    removeItemHandler(company);
  };

  const updatePrimaryClients = () => {
    let newVal = primaryClients;
    if (isPrimary) {
      newVal = newVal.filter((item) => item.id !== company.id);
    } else {
      newVal = [...newVal, company];
    }
    form.setValue('primary_client', newVal, { shouldDirty: true });
  };

  return (
    <div className="border-c-border-muted text-accent flex h-11 flex-nowrap items-center justify-between border-b dark:text-white">
      <div className="flex items-center gap-1.5">
        <Link
          to="/companies/$id"
          params={{ id: `${id}` }}
          state={{ naturalBack: true }}
          className="group flex items-center gap-2"
        >
          <IconLogoAvatar src={logo?.at(0)?.url} name={fullName} avatarShape="rectangle" />
          <span className="decoration-primary line-clamp-2 text-sm decoration-2 group-hover:underline">{fullName}</span>
        </Link>
      </div>
      <div className="flex items-center gap-1.5">
        {(isEditable || isPrimary) && canSetPrimary ? (
          <Button
            icon={isPrimary ? FilledStar : StarIcon}
            tag="div"
            as="link"
            size="sm"
            aria-label="Company Type"
            iconClassName="text-gray-400"
            className={!isPrimary ? 'opacity-40 hover:opacity-100' : undefined}
            disabled={!isEditable}
            onClick={isEditable ? updatePrimaryClients : undefined}
          />
        ) : null}
        {isEditable && (
          <DeleteButton
            type="icon"
            size="sm"
            title="Remove Company?"
            text="The Company record will not be deleted; only its association will be removed."
            yesButton="Yes, Remove"
            aria-label="Remove company"
            callback={handleDeleteContact}
          />
        )}
      </div>
    </div>
  );
}

export const CompanyItem = memo(CompanyItemBase);
